import {
  ChakraFlex,
  coalescePlainTextToEditorV2Html,
  EditorV2,
  Text,
  TextAreaInputV2,
  ValidationMessage,
} from '@equip.health/ui';
import { useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { gql, useSubscription } from '@apollo/client';
import { useEffect, useState } from 'react';

import { EditNoteFormData } from '~/lib/types/notes/EditNoteFormData';
import { useEditPatientNoteFormContext } from '~/lib/contexts/EditPatientNoteFormContext';
import { PatientNoteAiSummaryBanner } from './aiSummaryBanner/PatientNoteAiSummaryBanner';
import { useLazyGetAigssByAppointmentIdQuery } from '~/store/services/notes/aiGeneratedNoteSessionSummary';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { NoteEvents } from '~/analytics/notesEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsEvent } from '~/analytics';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { GetAigssResponse } from '~/lib/types/notes/GetAigssResponse';
import {
  useProcessAigssEventsDispatch,
  useProcessedAigssEventsSelector,
} from '~/store/reducers/processedAigssEvents';
import { AISummaryEvents } from '~/analytics/aiSummaryEvents';
import { ValidationType } from '~/lib/constants/enumeration.constants';

const ON_AIGSS_UPDATE_FOR_APPOINTMENT = gql`
  subscription OnAIGSSUpdateForAppointment(
    $providerId: String!
    $appointmentId: String!
  ) {
    onAIGSSUpdateForAppointment(
      providerId: $providerId
      appointmentId: $appointmentId
    ) {
      id
      creationDate
      status
      text
      appointmentId
      providerId
    }
  }
`;

const TOP_LEVEL_ID = 'patient-note-ai-summary';

const PATIENT_NOTE_SESSION_SUMMARY_MAX_HEIGHT = '400px';

// 'Follow-Up - Recovery Coach' and 'Follow-Up - Caregiver Coach'
const COACH_FOLLOW_UP_TEMPLATE_CODES = ['FUP_PEER_MENTOR', 'FUP_FAMILY_MENTOR'];

// 'Follow-Up - Med Team'
const MED_TEAM_TEMPLATE_CODES = ['FUP_MED_TEAM'];

type Props = {
  noteTemplate: NoteTemplate;
};

const DEFAULT_SUMMARY_PLACEHOLDER_TEXT =
  "Please provide a narrative summary of session, including any key discussion points, the participants' motivation level(s), and notable information for the team";
const AI_SUMMARY_PLACEHOLDER_TEXT =
  'Feel free to use this text box as a scratchpad while waiting for the AI summary. Your notes will not be deleted once that is ready.';

export const PatientNoteSessionSummarySection = ({ noteTemplate }: Props) => {
  const { values: formValues } = useFormikContext<EditNoteFormData>();
  const { currentUser } = useGlobalContext();

  const {
    initialValues,
    setFormField,
    fieldErrors,
    setFieldErrors,
    sessionSummaryEditorRef,
    sessionSummarySectionRef,
    setHasUnsavedChanges,
  } = useEditPatientNoteFormContext();

  const clearSummaryError = () => {
    if (fieldErrors.summaryError) {
      setFieldErrors((prev) => ({ ...prev, summaryError: null }));
    }
  };

  const {
    sessionSummaryRichText: isSessionSummaryRichTextEnabled,
    aiSummaryAppSyncPoc: isAiSummaryAppSyncEnabled,
    aigssBannersForDietaryBetaTest: isAigssBannerForDietaryEnabled,
    aigssCoachNoteTemplate: isAigssBannerForCoachEnabled,
    aigssMedNoteTemplate: isAigssBannerForMedTeamEnabled,
  } = useFlags<LDFlags>();

  const { trackEvent } = useAnalytics();

  const { ids: processedAigssEventIds } = useProcessedAigssEventsSelector();
  const { processAigssEventId } = useProcessAigssEventsDispatch();

  const [currentAigssSummary, setCurrentAigssSummary] =
    useState<GetAigssResponse | null>(null);

  const sendAnalyticsEvent = (analyticsEvent: AnalyticsEvent) => {
    trackEvent(analyticsEvent, {
      Source: AnalyticsSourceValues.SESSION_GUIDE,
    });
  };

  const [
    getAigssByAppointmentId,
    { data: initialAigssSummary, isFetching: isRefreshAigssFetching },
  ] = useLazyGetAigssByAppointmentIdQuery();

  const appointmentId = formValues.appointment?.node;

  useEffect(() => {
    if (initialAigssSummary) {
      setCurrentAigssSummary(initialAigssSummary);
    }
  }, [initialAigssSummary]);

  useSubscription(ON_AIGSS_UPDATE_FOR_APPOINTMENT, {
    onData: ({ data: eventData }) => {
      const event = eventData.data?.onAIGSSUpdateForAppointment;
      if (
        event &&
        !processedAigssEventIds[event.id] &&
        event.id !== initialAigssSummary?.id
      ) {
        setCurrentAigssSummary(event);
        processAigssEventId(event.id);
        sendAnalyticsEvent(AISummaryEvents.aiSummaryRealtimeUpdateReceived);
      }
    },
    skip: !isAiSummaryAppSyncEnabled || !appointmentId || !currentUser?.userId,
    variables: {
      appointmentId,
      providerId: currentUser?.userId,
    },
  });

  const aigssSummaryToDisplay = isAiSummaryAppSyncEnabled
    ? currentAigssSummary
    : initialAigssSummary;

  const isCoachFollowUpNote = COACH_FOLLOW_UP_TEMPLATE_CODES.includes(
    noteTemplate.templateCode,
  );

  // With the flag off, a coach template should look as though AI summary does
  // not exist for it — no banner, and no placeholder promising a summary that
  // will never arrive.
  const isVisibleForCoachNoteTest =
    !isCoachFollowUpNote || isAigssBannerForCoachEnabled;

  const isMedTeamNote = MED_TEAM_TEMPLATE_CODES.includes(
    noteTemplate.templateCode,
  );

  const isVisibleForMedTeamNoteTest =
    !isMedTeamNote || isAigssBannerForMedTeamEnabled;

  const showAiSummaryPlaceholderText =
    noteTemplate.hasAiSummary &&
    appointmentId &&
    isVisibleForCoachNoteTest &&
    isVisibleForMedTeamNoteTest &&
    (formValues.aiSummaryId || aigssSummaryToDisplay?.status !== 'FAILURE');

  const placeholderText = showAiSummaryPlaceholderText
    ? AI_SUMMARY_PLACEHOLDER_TEXT
    : DEFAULT_SUMMARY_PLACEHOLDER_TEXT;

  useEffect(() => {
    if (appointmentId) {
      getAigssByAppointmentId(appointmentId);
    }
  }, [appointmentId]);

  const htmlInitialSessionSummary = coalescePlainTextToEditorV2Html(
    initialValues?.sessionSummary ?? '',
  );
  const htmlSessionSummary = formValues.sessionSummary
    ? coalescePlainTextToEditorV2Html(formValues.sessionSummary)
    : null;

  useEffect(() => {
    sessionSummaryEditorRef.current
      ?.chain()
      .setContent(htmlSessionSummary)
      .run();
  }, [htmlSessionSummary]);

  const isFupDietaryNote = noteTemplate.templateCode === 'FUP_DIETARY';

  const isVisibleForDietaryNoteTest =
    !isFupDietaryNote || isAigssBannerForDietaryEnabled;

  return (
    <ChakraFlex
      ref={sessionSummarySectionRef}
      flexDirection="column"
      marginBottom="36px"
      marginTop="16px"
      padding="1px"
    >
      <Text variant="h5">Session summary</Text>
      {appointmentId &&
        aigssSummaryToDisplay &&
        noteTemplate.hasAiSummary &&
        isVisibleForDietaryNoteTest &&
        isVisibleForCoachNoteTest &&
        isVisibleForMedTeamNoteTest &&
        (isAiSummaryAppSyncEnabled ? (
          <PatientNoteAiSummaryBanner aigssSummary={aigssSummaryToDisplay} />
        ) : (
          <PatientNoteAiSummaryBanner
            aigssSummary={aigssSummaryToDisplay}
            isRefreshAigssFetching={isRefreshAigssFetching}
            refreshAigss={() => getAigssByAppointmentId(appointmentId)}
          />
        ))}
      {!isSessionSummaryRichTextEnabled && (
        <TextAreaInputV2
          fitContent
          id={TOP_LEVEL_ID}
          isInvalid={!!fieldErrors.summaryError}
          maxHeight={PATIENT_NOTE_SESSION_SUMMARY_MAX_HEIGHT}
          onBlur={() => {
            sendAnalyticsEvent(NoteEvents.focusOffNoteSummaryField);
          }}
          onChange={(val) => setFormField('sessionSummary', val)}
          onFocus={() => {
            sendAnalyticsEvent(NoteEvents.focusOnNoteSummaryField);
          }}
          placeholder={placeholderText}
          value={formValues.sessionSummary || ''}
        />
      )}
      {isSessionSummaryRichTextEnabled && (
        <EditorV2
          containerTestId="patient-note-session-summary-textbox"
          initialContent={htmlInitialSessionSummary}
          isInvalid={!!fieldErrors.summaryError}
          onBlur={() => sendAnalyticsEvent(NoteEvents.focusOffNoteSummaryField)}
          onChange={() => {
            setHasUnsavedChanges(true);
            // EditorV2 fires no native change event, so <Form onChange> never
            // re-validates this field for us
            clearSummaryError();
          }}
          onFocus={() => {
            sendAnalyticsEvent(NoteEvents.focusOnNoteSummaryField);
          }}
          placeholderText={placeholderText}
          stateRef={sessionSummaryEditorRef}
        />
      )}
      {fieldErrors.summaryError && (
        <ValidationMessage
          id="patient-note-session-summary-error"
          role="alert"
          type={ValidationType.CRITICAL}
          value={fieldErrors.summaryError}
        />
      )}
    </ChakraFlex>
  );
};
